import ConfirmationPopup from '@/components/ConfirmationPopup';
import EmptyPageButton from '@/components/EmptyPageButton';
import { SaveTemplatePopupContext } from '@/components/SaveTemplatePopup/contextProvider';
import UpgradeButton from '@/components/UpgradeButton';
import { PageOptionContext, PageOptionDispatchContext } from '@/components/dashboard/context';
import { DUPLICATE_PAGE_URL } from '@/constants';
import { useSpaceAndPageIDFromRouter } from '@/data/space/hooks';
import CopyPasteInput from '@/sharedComponents/TextEditor/components/CopyPasteInput';
import {
  useDeletePageMutation,
  useDuplicatePageMutation,
  useListPagesQuery,
  useUpdatePageMetadataMutation,
  useUpdateRoutesMutation,
  useUpdateSpaceCustomizationPartialMutation,
} from '@/store';
import sharedStyles from '@/styles/Shared.module.css';
import { checkRouteChildren } from '@/utils/checkRouteChildren';
import getPageRouteFromPath from '@/utils/getPageRouteFromPath';
import { removeChildById } from '@/utils/removeChildById';
import useCurrentPagePath from '@/utils/useCurrentPagePath';
import { CaretRight, CopySimple, House, Layout, Trash } from '@phosphor-icons/react';
import {
  DEFAULT_NEW_PAGE_SLUG_RANDOM_LENGTH,
  FormExtractor,
  ICustomizationUpdateRequest,
  IterateContent,
  errToString,
  makeValidPath,
  mergeSpaceCustomization,
  validUUID,
} from '@typedream/data';
import { Button, Text } from '@typedream/ui';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { PageMetadataTab, PagesOptionSettingProps } from './types';

export function PagesOptionSetting({
  setOpen,
  setMetadataTab,
  upgradeRequired,
  enableEmpty,
  enableDelete,
}: PagesOptionSettingProps) {
  const router = useRouter();
  const [spaceID, pageID] = useSpaceAndPageIDFromRouter();
  const [subpages, setSubpages] = useState<number | null>(null);
  const [isColParentPage, setIsColParentPage] = useState<boolean>(false);
  const [isOpeningTemplate, setIsOpeningTemplate] = useState<boolean>(false);
  const [isShowDeletePageModal, setIsShowDeletePageModal] = useState<boolean>(false);

  const { space, page, path, settings } = useContext(PageOptionContext);
  const { setSettings, onCancel } = useContext(PageOptionDispatchContext);
  const { typedreamSubdomain } = useCurrentPagePath();
  const {
    setopen: setSaveTemplateOpen,
    setcontent: setSaveTemplateContent,
    setcustomization: setSaveTemplateCustomization,
    setPageID,
  } = useContext(SaveTemplatePopupContext);
  const { data: pages } = useListPagesQuery([spaceID]);
  const { mutate: updateRoutes } = useUpdateRoutesMutation();
  const { mutate: duplicatePage, isLoading: loadingDuplicatePage } = useDuplicatePageMutation();
  const { mutate: updatePageMetadata } = useUpdatePageMetadataMutation();
  const { mutate: deletePage, isLoading: loadingDeletePage } = useDeletePageMutation();
  const { mutate: updateSpaceCustomizationPartial } = useUpdateSpaceCustomizationPartialMutation();

  const home = useMemo(() => path?.length === 0, [path]);
  const isCurrentPage = useMemo(() => page?.id && page?.id === pageID, [page, pageID]);
  const pathParent = useMemo(() => (path || []).slice(0, -1), [path]);
  const pathParentURL = useMemo(
    () => (pathParent && pathParent.length > 0 ? `/${pathParent?.join('/')}` : ''),
    [pathParent]
  );
  const slugURL = useMemo(() => (settings.slug ? `/${settings.slug}` : ''), [settings.slug]);
  const customdomainURL = useMemo(
    () => `https://${space?.custom_domain}${pathParentURL}${slugURL}`,
    [space, pathParentURL, slugURL]
  );
  const localURL = useMemo(
    () => `https://${typedreamSubdomain}${pathParentURL}${slugURL}`,
    [typedreamSubdomain, pathParentURL, slugURL]
  );

  useEffect(() => {
    if (page) {
      setSubpages(null);
      setIsColParentPage(false);
      checkRouteChildren(space?.routes?.children || {}, ([, value]) => {
        if (value.page_id === page?.id) {
          if (value?.children_template_page_id && validUUID(value?.children_template_page_id)) {
            setIsColParentPage(true);
          }
          if (value?.children_keys?.length) {
            setSubpages(value?.children_keys?.length);
          }
        }
      });
    }
  }, [page, space]);

  const openSaveTemplate = () => {
    setIsOpeningTemplate(true);
    setPageID(page?.id || null);
    setSaveTemplateContent(page?.content || []);

    const formExtractor = new FormExtractor(space.customization);
    IterateContent(page?.content || [], [formExtractor]);

    const templateCustomization = formExtractor.getCustomization();
    if (setSaveTemplateCustomization) {
      setSaveTemplateCustomization((curr) => {
        if (!curr) return templateCustomization;
        return mergeSpaceCustomization(curr, templateCustomization);
      });
    }
    setIsOpeningTemplate(false);

    setOpen(false);
    setSaveTemplateOpen(true);
  };

  const onSetHomepage = async () => {
    if (space) {
      if (subpages) {
        toast.error('please remove all subpages inside this page before making it as homepage');
        return;
      }
      if (isColParentPage) {
        toast.error(
          'Cannot use the Collection Parent Page as your Home Page. Please try other pages.'
        );
        return;
      }
      const oldRoutes = { ...space.routes };
      const newRoutes = removeChildById(oldRoutes, page?.id);

      const randString = Math.random()
        .toString(36)
        .substring(5, 5 + DEFAULT_NEW_PAGE_SLUG_RANDOM_LENGTH);
      const newSlug = `page-${randString}`;

      newRoutes.page_id = page?.id;
      newRoutes.children_keys = [newSlug, ...newRoutes.children_keys];
      newRoutes.children[newSlug] = { page_id: space.routes.page_id };

      await updateRoutes({ spaceID: space.id, routes: newRoutes }).unwrap();
    }
  };

  const openMetadata = (isAllowed: boolean, tab: PageMetadataTab) => {
    if (isAllowed) {
      setMetadataTab(tab);
    }
  };

  const onDuplicate = async () => {
    try {
      const currentSpaceChild = space.routes.children;
      const newSpace = await duplicatePage({
        create: false,
        destinationSpaceID: space?.id,
        slug: space?.slug,
        pagePath: path,
        duplicateOwnPage: true,
      }).unwrap();
      if (page?.metadata?.page_type === 'link_in_bio') {
        const newSpaceChild = newSpace.routes.children;
        const newPageKey = Object.keys(newSpaceChild).filter(
          (key) => currentSpaceChild[key] === undefined
        )[0];
        const newPageId = newSpace.routes.children[newPageKey].page_id;
        if (newSpace && validUUID(newPageId)) {
          await updatePageMetadata({
            spaceID: space?.id,
            pageID: newPageId,
            metadata: {
              ...page.metadata,
              page_type: 'link_in_bio',
            },
            newTwitterImage: null,
            newFacebookImage: null,
          }).unwrap();
        }
      }
    } catch (error) {
      toast.error(errToString(error));
    }
  };

  const onDelete = async () => {
    let pageSlug = '';
    let pagePath = [];
    if (path && path.length > 0) {
      pageSlug = path[path.length - 1];
      if (path.length > 1) {
        pagePath = path.slice(0, -1);
      } else {
        pagePath = [];
      }
    }

    try {
      await deletePage({
        spaceID,
        pageID: page.id,
        slug: pageSlug,
        pagePath,
      }).unwrap();

      const routeOnPath = getPageRouteFromPath(space?.routes, path);
      const collectionItemData = space?.customization?.collection_item_data;
      if (
        routeOnPath?.children_template_page_id &&
        validUUID(routeOnPath?.children_template_page_id) &&
        collectionItemData
      ) {
        const customizationUpdateRequests: ICustomizationUpdateRequest[] = [];

        Object.entries(collectionItemData)?.forEach(([k, v]) => {
          if (v?.childrenTemplatePageID === routeOnPath?.children_template_page_id) {
            customizationUpdateRequests.push({
              customization_path: ['collection_item_data', k],
              customization_value: null,
              customization_delete: true,
            });
          }
        });
        await updateSpaceCustomizationPartial({ spaceID, customizationUpdateRequests }).unwrap();
      }
      onCancel();
      const otherPageId = Object.keys(pages).find((pageId) => pageId !== page.id);
      router.push(`/s/${space.slug}/${spaceID}/${otherPageId}`);
      setOpen(false);
    } catch (error) {
      toast.error(errToString(error));
    }
  };

  return (
    <div className="flex flex-col gap-5 w-96 px-5">
      <div className="flex flex-col gap-2">
        <Text size="sm" weight="medium" variant="secondary" className="tracking-[-0.01em]">
          Title
        </Text>
        <input
          className={classNames(sharedStyles.inputMedium, 'mb-4')}
          type="text"
          placeholder="e.g. Home"
          value={settings.name}
          onChange={(e) =>
            setSettings((curr) => ({
              ...curr,
              name: e.target.value,
            }))
          }
        />
      </div>
      <div className="flex flex-col gap-2">
        <Text size="sm" weight="medium" variant="secondary" className="tracking-[-0.01em]">
          Page URL
        </Text>
        {!home ? (
          <>
            <input
              className={sharedStyles.inputMedium}
              type="text"
              placeholder="e.g. home"
              value={settings.slug}
              onChange={(e) =>
                setSettings((curr) => ({
                  ...curr,
                  slug: makeValidPath(e.target.value),
                }))
              }
            />
            <Text size="2xs" variant="secondary" className="mb-4">
              {space?.custom_domain ? customdomainURL : localURL}
            </Text>
            <Button
              variant="secondary"
              leftIcon={<House size={16} weight="bold" />}
              size="xs"
              onClick={onSetHomepage}
              className="self-start"
            >
              Set as home
            </Button>
          </>
        ) : (
          <Text size="2xs" variant="secondary">
            {space?.custom_domain ? customdomainURL : localURL}
          </Text>
        )}
      </div>
      <hr className="bg-transparent border-t border-t-border" />
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <Text size="sm" weight="medium" variant="secondary" className="tracking-[-0.01em]">
            Share to web
          </Text>
          <div className="flex items-center">
            <input
              id="public"
              type="checkbox"
              name="public"
              className="switch is-rounded is-small is-primary pointer-events-none"
              checked={settings.public}
              onChange={() =>
                setSettings((curr) => ({
                  ...curr,
                  duplicate: curr.public ? false : curr.duplicate,
                  public: !curr.public,
                }))
              }
            />
            <label htmlFor="public"> </label>
          </div>
        </div>
        <Text size="2xs" variant="secondary">
          Anyone with the link can view this page
        </Text>
      </div>
      <div
        className={classNames(
          'flex flex-col gap-2',
          !settings.public ? 'opacity-30 pointer-events-none' : ''
        )}
      >
        <div className="flex items-center justify-between">
          <Text size="sm" weight="medium" variant="secondary" className="tracking-[-0.01em]">
            Allow duplicate
          </Text>
          <div className="flex items-center">
            <input
              id="duplicate"
              type="checkbox"
              name="duplicate"
              className="switch is-rounded is-small is-primary pointer-events-none"
              checked={settings.duplicate}
              onChange={() =>
                setSettings((curr) => ({
                  ...curr,
                  duplicate: !curr.duplicate,
                }))
              }
            />
            <label htmlFor="duplicate"> </label>
          </div>
        </div>
        <Text size="2xs" variant="secondary">
          Anyone with the link below can duplicate this page
        </Text>
        {settings.duplicate && (
          <CopyPasteInput value={`${DUPLICATE_PAGE_URL}${encodeURIComponent(localURL)}`} />
        )}
      </div>
      <div
        className={classNames(
          'flex flex-col gap-2 items-start',
          !settings.public ? 'opacity-30 pointer-events-none' : ''
        )}
      >
        <Text size="sm" weight="medium" variant="secondary" className="tracking-[-0.01em]">
          Save as a template
        </Text>
        <Text size="2xs" variant="secondary">
          Your team on this site can use it later by clicking &quot;Start with a template&quot; on
          an empty page.
        </Text>
        <Button
          variant="secondary"
          size="xs"
          className="mt-2"
          leftIcon={<Layout size={14} weight="bold" />}
          onClick={openSaveTemplate}
          isLoading={isOpeningTemplate}
        >
          Save Template
        </Button>
      </div>
      <hr className="bg-transparent border-t border-t-border" />
      <Text size="sm" weight="medium" variant="secondary" className="tracking-[-0.01em]">
        SEO & Sharing
      </Text>
      {upgradeRequired && (
        <div className="self-start">
          <UpgradeButton slug={space?.slug} spaceID={space?.id as string} />
        </div>
      )}
      <div
        className={classNames(
          'flex flex-col gap-5',
          upgradeRequired && 'opacity-30 pointer-events-none'
        )}
      >
        <button
          type="button"
          className="flex items-center justify-between gap-4 p-0 pb-4 text-left bg-transparent border-0 border-b border-b-border border-solid cursor-pointer"
          onClick={() => {
            openMetadata(!upgradeRequired, PageMetadataTab.METADATA);
          }}
        >
          <div className="flex flex-col gap-2">
            <Text size="sm" weight="regular" variant="primary-soft" className="tracking-[-0.01em]">
              Metadata
            </Text>
            <Text size="2xs" variant="secondary">
              Extra content for search engines
            </Text>
          </div>
          <CaretRight size={20} className="text-primary-soft" />
        </button>
        <button
          type="button"
          className="flex items-center justify-between gap-4 p-0 pb-4 text-left bg-transparent border-0 border-b border-b-border border-solid cursor-pointer"
          onClick={() => {
            openMetadata(!upgradeRequired, PageMetadataTab.TWITTER);
          }}
        >
          <div className="flex flex-col gap-2">
            <Text size="sm" weight="regular" variant="primary-soft" className="tracking-[-0.01em]">
              Twitter Card
            </Text>
            <Text size="2xs" variant="secondary">
              Customize data for Twitter card
            </Text>
          </div>
          <CaretRight size={20} className="text-primary-soft" />
        </button>
        <button
          type="button"
          className="flex items-center justify-between gap-4 p-0 pb-4 text-left bg-transparent border-0 border-b border-b-border border-solid cursor-pointer"
          onClick={() => {
            openMetadata(!upgradeRequired, PageMetadataTab.FACEBOOK);
          }}
        >
          <div className="flex flex-col gap-2">
            <Text size="sm" weight="regular" variant="primary-soft" className="tracking-[-0.01em]">
              Facebook Card
            </Text>
            <Text size="2xs" variant="secondary">
              Customize OpenGraph data
            </Text>
          </div>
          <CaretRight size={20} className="text-primary-soft" />
        </button>
      </div>
      <Button
        variant="secondary"
        className="self-start mt-4"
        leftIcon={<CopySimple size={20} />}
        isLoading={loadingDuplicatePage}
        onClick={onDuplicate}
      >
        Duplicate Page
      </Button>
      {(enableEmpty || enableDelete) && (
        <div className="flex flex-col items-start gap-5 border-0 border-t border-solid border-border mt-4 pt-4">
          {enableEmpty && isCurrentPage && <EmptyPageButton />}
          {enableDelete && !home && (
            <>
              <Button
                leftIcon={
                  loadingDeletePage ? (
                    <span className="loader is-loading mr-2 with-accent-border-color has-text-danger" />
                  ) : (
                    <Trash size={18} className="mr-2 is-subtitle-size" />
                  )
                }
                variant="destructive-soft"
                onClick={
                  loadingDeletePage
                    ? () => {
                        /** do nothing */
                      }
                    : () => {
                        setIsShowDeletePageModal(true);
                      }
                }
              >
                Delete Page
              </Button>
              <ConfirmationPopup
                btnLabel="Delete Page"
                description={<>This action cannot be undone.</>}
                title="Delete this page?"
                isShow={isShowDeletePageModal}
                handleBtnClick={onDelete}
                setIsShow={setIsShowDeletePageModal}
                btnClassNames="is-danger"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
